// The agent's "nothing to state here" marker, as the renderers read it.
const PLACEHOLDER_TOKEN = '__PLACEHOLDER__';
const PLACEHOLDER_PREFIX = `${PLACEHOLDER_TOKEN}:`;

/** The JCA note's headline when an assessment is actually required — HEADLINE_LIKELY in
 *  app/dossier/eu_medicinal/rules/hta_jca.py, character for character. The cover row appears
 *  only for this headline; a future-wave heads-up belongs in the eAF note. Gating on a
 *  shorter prefix than the PDF let the two covers diverge on a wording change. */
const JCA_HEADLINE_LIKELY = 'JCA likely required — confirm.';

/** One row of the EU CTD cover's identity block. */
export interface CtdIdentityRow {
  label: string;
  value: string | null;
  /** Shown as the "Submitter must complete" chip when `value` is not on file. */
  hint: string;
}

/** The EU CTD's identity rows — what a reviewer establishes before reading anything else:
 *  which product, which substance, which applicant, which procedure.
 *
 *  The medicinal counterpart of `combinedIdentity.ts`, built from what a MEDICINAL review
 *  needs rather than by mirroring the device rows — no SRN, no authorised representative, no
 *  UDI. Rows that do not apply are left out: the designations line without any on file, the
 *  HTA line unless the product is in scope.
 *
 *  LOCKSTEP with the PDF cover's `cover_identity_rows` in
 *  backend/services/dossier/ectd_renderer/eu.py: same labels, same hints, same order. */
export function ctdIdentityRows(
  af: Record<string, unknown> | null | undefined,
  opts: {
    legalBasisLabel?: string | null;
    productName?: string | null;
    productType?: string | null;
    /** The run's company — the PDF's fallback when the eAF has no stamped applicant. */
    company?: string | null;
  },
): CtdIdentityRow[] {
  /** The stated value, or null when the eAF has nothing real for this key.
   *
   *  A WHOLE-VALUE placeholder marker counts as nothing — the model writes
   *  "__PLACEHOLDER__:Proposed (invented) name" when it cannot read a value, and treating
   *  that as stated skipped the fall-backs below, so the page asked for the product name the
   *  run was dispatched with. Mirrors the PDF's `_val` (ectd_renderer/eu.py). A marker INSIDE
   *  a real value is left alone; it renders as a chip mid-sentence. */
  const v = (key: string): string | null => {
    const raw = af?.[key];
    const text = typeof raw === 'string' ? raw.trim() : '';
    if (!text) return null;
    if (text.startsWith(PLACEHOLDER_PREFIX) || text === PLACEHOLDER_TOKEN) return null;
    return text;
  };
  const procedureNumberHint = (): string => {
    // Mirrors procedure_number_hint: the shape to expect for the stamped procedure.
    const label = v('procedure') ?? '';
    const rms = /RMS: ([A-Z]{2})\b/.exec(label)?.[1] ?? 'RMS';
    if (label.startsWith('Decentralised procedure')) return `Decentralised procedure number (${rms}/H/xxxx/001/DC)`;
    if (label.startsWith('Mutual Recognition procedure')) return `Mutual recognition procedure number (${rms}/H/xxxx/001/MR)`;
    if (label.startsWith('National procedure')) {
      const state = /Member State: ([A-Z]{2})\b/.exec(label)?.[1];
      return `National marketing authorisation application number${state ? ` (${state})` : ''}`;
    }
    return 'EMA procedure number for this application (EMEA/H/C/xxxxxx/0000, or …/II/xxxx for a variation)';
  };

  const rows: CtdIdentityRow[] = [
    {
      // The eAF's own (invented) name — the dispatch name is our internal identifier, and read
      // as the product name on a marketing authorisation it is simply wrong.
      label: 'Product name',
      value: v('product_name') ?? (opts.productName?.trim() || null),
      hint: '(Invented) name of the medicinal product',
    },
    {
      label: 'Active substance(s) / INN',
      value: v('active_substance'),
      hint: 'Active substance, by its international non-proprietary name',
    },
    {
      label: 'Pharmaceutical form and strength',
      value: v('pharmaceutical_form_strength'),
      hint: 'Pharmaceutical form and strength per Standard Terms',
    },
    { label: 'ATC code', value: v('atc_code'), hint: 'ATC code, or the status of the request to the WHO' },
    { label: 'Product type', value: opts.productType?.trim() || null, hint: 'drug or biologic' },
    {
      // Runs from before the applicant stamp carry none — fall back to the run's company, as
      // the PDF cover does, or the same dossier names the applicant in one place and asks for
      // it in the other.
      label: 'Applicant (MAH)',
      value: v('applicant') ?? (opts.company?.trim() || null),
      hint: 'EU-established applicant (MAH) name and address',
    },
    {
      label: 'Qualified Person for Pharmacovigilance (QPPV)',
      value: v('qppv'),
      hint: 'QPPV name and contact (Article 104, Directive 2001/83/EC)',
    },
    {
      label: 'Legal basis',
      value: opts.legalBasisLabel?.trim() || null,
      hint: 'Directive 2001/83/EC Article',
    },
    {
      label: 'Procedure',
      value: v('procedure'),
      hint: 'Centralised / Decentralised / Mutual Recognition / National',
    },
    { label: 'Procedure number', value: v('procedure_number'), hint: procedureNumberHint() },
  ];

  // The EMA product number, centralised only. It stays the same for the life of the
  // marketing authorisation while the procedure number above names this application
  // (RA, 2026-10-26). LOCKSTEP with cover_identity_rows.
  if ((v('procedure') ?? '').startsWith('Centralised procedure')) {
    rows.push({
      label: 'EMA product number',
      value: v('product_number'),
      hint: 'EMA product number (EMEA/H/C/xxxxxx)',
    });
  }

  const designations = af?.['designations'];
  if (Array.isArray(designations) && designations.length > 0) {
    rows.push({
      label: 'Designations and opinions',
      value: designations.map((d) => String(d)).join('; '),
      hint: '',
    });
  }

  // Only when something is actually required, and named as REGARA's own observation — a
  // "not yet in scope" line is internal advice, and belongs in the eAF note rather than on
  // the cover. LOCKSTEP with cover_identity_rows.
  const jca = v('hta_jca_note');
  if (jca && jca.startsWith(JCA_HEADLINE_LIKELY)) {
    rows.push({ label: 'EU HTA JCA scope (REGARA note)', value: `${jca.split('.')[0]}.`, hint: '' });
  }

  // Reg. (EC) No 726/2004 governs the CENTRALISED procedure only; asserting it on a national
  // or decentralised cover states the wrong legal framework.
  rows.push({
    label: 'Reference',
    value: (v('procedure') ?? '').startsWith('Centralised procedure')
      ? 'Directive 2001/83/EC; Regulation (EC) No 726/2004'
      : 'Directive 2001/83/EC',
    hint: '',
  });
  return rows;
}
